import type { TachoActivityDayDto } from '@oip/contracts';

/**
 * The activity chart, as words and colours.
 *
 * Pure and separately tested, because on this card the wording IS the product:
 * every sentence here is the difference between a chart a transport manager can
 * act on and one that quietly asserts something nobody measured.
 */

export const ACTIVITY_STATES = [3, 2, 1, 0] as const;
export type ActivityState = (typeof ACTIVITY_STATES)[number];

/**
 * ⚠️ **The INDUSTRY colours, decided 12 September 2026 — and they break this
 * product's palette rule on purpose.**
 *
 * Every tachograph analysis tool draws driving in RED, rest in BLUE, other work
 * in GREEN and availability in AMBER, and a transport manager reads that scheme
 * daily. This card first used `hues.ts` — the palette adjacent to nothing
 * meaningful — because green, amber and red are the four RISK LEVELS on every
 * badge and every score here, so a red band risks reading as *driving is a
 * problem*. That concern was raised and OVERRULED: matching what the reader
 * already knows wins, and an unfamiliar scheme on a chart people read by colour
 * is its own kind of wrong answer.
 *
 * The cost is stated rather than left to be discovered: this is the ONE surface
 * in the product where green, amber and red do not mean a risk level, and a
 * reader arriving from an incident badge could carry that reading across. Two
 * things keep it contained.
 *
 * ⚠️ **The values are LITERALS here — never `theme.palette.error`, never
 * `hues.ts`.** Pulling from the risk palette would make this chart repaint
 * whenever somebody retunes a severity colour, silently, and in the direction
 * that makes the coupling real rather than coincidental. These are the
 * tachograph's colours that happen to resemble those, not those reused.
 *
 * ⚠️ **The legend names every band in words, and that is now load-bearing**
 * rather than a courtesy: colour carries a convention here, so a reader who
 * cannot tell red from green must still be able to read the chart.
 */
const STATES: Record<ActivityState, { label: string; short: string; light: string; dark: string }> =
  {
    3: { label: 'Driving', short: 'Drive', light: '#D32F2F', dark: '#F0625E' },
    2: { label: 'Other work', short: 'Work', light: '#2E7D32', dark: '#66BB6A' },
    1: { label: 'Available', short: 'Avail.', light: '#ED6C02', dark: '#FFA726' },
    0: { label: 'Rest', short: 'Rest', light: '#1976D2', dark: '#5AAAF5' },
  };

export function stateLabel(state: number): string {
  return STATES[state as ActivityState]?.label ?? `State ${state}`;
}

/**
 * The band colour for a state.
 *
 * ⚠️ An unrecognised code falls back to a NEUTRAL grey rather than to any of
 * the four. 6 and 7 are the device's sentinels and the adapter maps them to
 * absence, so they should never arrive — but a row written before that mapping
 * must look unrecognised rather than be painted as an activity somebody was in.
 */
export function stateColor(state: number, dark: boolean): string {
  const s = STATES[state as ActivityState];
  if (!s) return dark ? '#9E9E9E' : '#757575';
  return dark ? s.dark : s.light;
}

export function stateShort(state: number): string {
  return STATES[state as ActivityState]?.short ?? String(state);
}

/** `4h 20m`, `55m`, `—` for nothing. Minutes are observed time, never a day's. */
export function duration(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** `hh:mm` from minutes past midnight — for a band's own start and end. */
export function clockAt(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = Math.floor(minute % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Is this row an ABSENCE rather than a quiet day?
 *
 * ⚠️ **The one distinction that cannot be read off the bands.** A day the
 * tachograph said nothing about and a day of solid rest draw the identical
 * empty row and mean opposite things — the device stopped, or the driver was
 * off. Everything else about a row's completeness is visible: the totals are
 * the observed time, and the track showing through is the rest.
 *
 * ⚠️ A per-row *"covers 6h 30m of recorded activity"* note was built here and
 * REMOVED after seeing it rendered: it restated the sum of the two figures
 * already on the row, one line per day, which pushed the rows apart and said
 * nothing the totals did not. Deleted rather than left dormant — scaffolding
 * kept in case is how it comes back.
 */
export function isSilent(day: TachoActivityDayDto): boolean {
  return day.reported === 0;
}

/** The four totals for a row, biggest state first and zeroes dropped. */
export function dayTotals(
  day: TachoActivityDayDto,
): Array<{ state: ActivityState; label: string; value: string }> {
  /*
   * ⚠️ EVERY state that has minutes, never a top-N. A row showing two of four
   * is a silent truncation on a figure somebody is reading to account for a
   * shift — and the one dropped would be the unusual one. A state with NO
   * minutes is omitted rather than shown as "—": four figures on every row,
   * three of them dashes, hides the one that moved.
   */
  return ACTIVITY_STATES.flatMap((state) => {
    const minutes = day.minutes[String(state)] ?? 0;
    if (minutes <= 0) return [];
    return [{ state, label: stateShort(state), value: duration(minutes) }];
  });
}
